import type { ICorridor } from "../shared/session/interfaces/session";
import { countryDisplayName } from "./country-display";

export interface CorridorPair {
  originCountryCode: string;
  destinationCountryCode: string;
}

/** Unique origin country codes from the link corridors (sorted). */
export const supportedOrigins = (corridors: ICorridor[]): string[] =>
  Array.from(new Set(corridors.map((c) => c.originCountryCode))).sort();

/** Destination codes reachable from the given origin (sorted). */
export const supportedDestinations = (
  corridors: ICorridor[],
  originCountryCode: string
): string[] =>
  Array.from(
    new Set(
      corridors
        .filter((c) => c.originCountryCode === originCountryCode)
        .map((c) => c.destinationCountryCode)
    )
  ).sort();

/**
 * Resolves the initial corridor from session config: pinned default, or the
 * first row in `config.corridors` (widget catalog order). Same contract as send.
 */
export const resolveInitialCorridor = (
  corridors: ICorridor[],
  defaultCorridor?: CorridorPair
): CorridorPair | undefined => {
  if (defaultCorridor) return defaultCorridor;
  const first = corridors[0];
  if (!first) return undefined;
  return {
    originCountryCode: first.originCountryCode,
    destinationCountryCode: first.destinationCountryCode,
  };
};

/** Origin country seed when compose store is empty. */
export const seedOriginCountry = (
  corridors: ICorridor[],
  defaultCorridor?: CorridorPair
): string | undefined =>
  resolveInitialCorridor(corridors, defaultCorridor)?.originCountryCode;

/** Destination country seed when compose store is empty. */
export const seedDestinationCountry = (
  corridors: ICorridor[],
  defaultCorridor?: CorridorPair
): string | undefined =>
  resolveInitialCorridor(corridors, defaultCorridor)?.destinationCountryCode;

/** Lookup a single corridor row for an origin/destination pair. */
export const findCorridor = (
  corridors: ICorridor[],
  originCountryCode: string,
  destinationCountryCode: string
): ICorridor | null =>
  corridors.find(
    (c) =>
      c.originCountryCode === originCountryCode &&
      c.destinationCountryCode === destinationCountryCode
  ) ?? null;

/** Minimal country row for corridor-driven pickers (no catalog fetch). */
export interface CorridorCountryOption {
  countryCode: string;
  countryName: string;
  currencyCode?: string;
  effectiveStatus: "AVAILABLE";
}

const FALLBACK_CURRENCY: Record<string, string> = {
  US: "USD",
  MX: "MXN",
  CO: "COP",
  GT: "GTQ",
  BR: "BRL",
  AR: "ARS",
  CL: "CLP",
  PE: "PEN",
  EC: "USD",
  DO: "DOP",
  HN: "HNL",
  SV: "USD",
};

export const currencyForCountryCode = (countryCode: string): string =>
  FALLBACK_CURRENCY[countryCode.toUpperCase()] ?? "USD";

export const countryOptionsFromCodes = (
  codes: string[],
  locale = "en"
): CorridorCountryOption[] =>
  codes.map((countryCode) => {
    const upper = countryCode.toUpperCase();
    return {
      countryCode: upper,
      countryName: countryDisplayName(upper, locale),
      currencyCode: currencyForCountryCode(upper),
      effectiveStatus: "AVAILABLE" as const,
    };
  });

/** One destination picker row per country+currency pair. */
export interface DestinationPairOption {
  countryCode: string;
  countryName: string;
  currencyCode: string;
  effectiveStatus: "AVAILABLE";
}

/**
 * Expand corridors for an origin into destination picker rows. Uses catalog
 * `destinationCurrencyCodes` when present; otherwise the local 1:1 map.
 */
export const expandCorridorDestinationOptions = (
  corridors: ICorridor[],
  originCountryCode: string,
  locale = "en"
): DestinationPairOption[] => {
  const rows: DestinationPairOption[] = [];
  const seen = new Set<string>();
  for (const corridor of corridors) {
    if (corridor.originCountryCode !== originCountryCode) continue;
    const dest = corridor.destinationCountryCode.toUpperCase();
    const listed = (corridor.destinationCurrencyCodes ?? [])
      .map((c) => c.trim().toUpperCase())
      .filter(Boolean);
    const currencies =
      listed.length > 0 ? [...new Set(listed)] : [currencyForCountryCode(dest)];
    for (const currencyCode of currencies) {
      const key = `${dest}-${currencyCode}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push({
        countryCode: dest,
        countryName: countryDisplayName(dest, locale),
        currencyCode,
        effectiveStatus: "AVAILABLE",
      });
    }
  }
  return rows;
};

/**
 * Pinned currency when offerable, else the country's local currency if allowed,
 * else the first allowed code. Do not sort alphabetically before picking.
 */
export const pickDefaultDestinationCurrency = (
  countryCode: string,
  allowed: string[],
  pinned?: string
): string | undefined => {
  if (allowed.length === 0) return undefined;
  const codes = allowed.map((c) => c.toUpperCase());
  const wanted = pinned?.trim().toUpperCase();
  if (wanted && codes.includes(wanted)) return wanted;
  const local = currencyForCountryCode(countryCode);
  if (codes.includes(local)) return local;
  return codes[0];
};
